import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { Aliases } from '../models/aliases';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { TranslationService } from '../services/translation.service';

/**
 * Centralizes alias value transformation with IsOverride awareness.
 * When IsOverride is true, the raw alias value is returned unchanged.
 * When IsOverride is false, applies the requested case transformation
 * and optional translation.
 *
 * Usage:
 *   {{ alias | aliasTransform }}
 *   {{ alias | aliasTransform:'titlecase' }}
 *
 * Supported case modifiers: 'none', 'titlecase', 'sentencecase', 'uppercase', 'lowercase'
 */
@Pipe({
    name: 'aliasTransform',
    pure: false,
    standalone: false
})
export class AliasTransformPipe implements PipeTransform, OnDestroy {
  private subscriptions = new Map<string, Subscription>();

  constructor(
    private titleCasePipe: TitleCasePipe,
    private translationService: TranslationService
  ) {}

  transform(
    alias: Aliases,
    caseModifier: string = 'none',
    translationRequest?: TranslationRequest
  ): string {
    if (!alias?.value) {
      return '';
    }

    if (alias.isOverride) {
      return alias.value;
    }

    const value = this.applyCase(alias.value, caseModifier);

    if (translationRequest) {
      return this.translationService.getTranslationForKey(value, translationRequest);
    }

    return value;
  }

  private applyCase(value: string, caseModifier: string): string {
    switch (caseModifier) {
      case 'titlecase':
        return this.titleCasePipe.transform(value);
      case 'sentencecase':
        return this.sentenceCase(value);
      case 'uppercase':
        return value.toUpperCase();
      case 'lowercase':
        return value.toLowerCase();
      default:
        return value;
    }
  }

  private sentenceCase(input: string): string {
    if (!input || input.length < 1) {
      return input;
    }
    const words = input.split(' ');
    let firstProcessed = false;
    return words
      .map((word) => {
        if (/\d/.test(word)) {
          return word;
        }
        if (!firstProcessed) {
          firstProcessed = true;
          return this.titleCasePipe.transform(word);
        }
        return word.toLowerCase();
      })
      .join(' ');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions.clear();
  }
}
